<template>
  <div>
    <Button
      data-testid="franquias-locations-add-button"
      icon="pi pi-plus"
      :label="t('Adicionar área de atuação')"
      severity="primary"
      @click="openSelectionModal"
      style="margin: 20px 0;" />

    <locations-list-view
      ref="locationsListRef"
      :franquia-id="franquiaId"
      :locations-data="(locationsData as Record<string, unknown>)"
      @remove-location="handleRemoveLocation">
    </locations-list-view>

    <!-- LocationsSelectionModal is centralized in ModalManager.vue - opened via eventBus -->
    <!-- LocationsStatesModal is centralized in ModalManager.vue - opened via eventBus -->
    <!-- LocationsCitiesModal is centralized in ModalManager.vue - opened via eventBus -->
    <!-- LocationsNeighborhoodsModal is centralized in ModalManager.vue - opened via eventBus -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocalidades } from '../../composables/useLocalidades';
import { eventBus } from '../../eventBus';
import LocationsListView from './LocationsListView.vue';

type Id = number | string;

interface AreasAtuacao {
  States?: unknown[];
  Cities?: unknown[];
  Neighborhoods?: unknown[];
  [key: string]: unknown;
}

interface FranquiaData {
  AreasAtuacao?: AreasAtuacao;
  [key: string]: unknown;
}

interface Props {
  franquiaId: number;
  franquiaData?: FranquiaData | null;
}

const props = withDefaults(defineProps<Props>(), {
  franquiaData: null,
});

const { t } = useI18n();
const { toggleRelacao } = useLocalidades();

// Refs
const locationsListRef = ref<unknown>(null);

// Computed
const locationsData = computed<AreasAtuacao>(() => {
  if (props.franquiaData && props.franquiaData.AreasAtuacao) {
    return props.franquiaData.AreasAtuacao;
  }
  return {
    States: [],
    Cities: [],
    Neighborhoods: [],
  };
});

// Methods
const openSelectionModal = (): void => {
  eventBus.emit('modal.locations-selection.open', {
    franquiaId: props.franquiaId,
    franquiaData: props.franquiaData,
  });
};

const refreshFranquia = async (): Promise<void> => {
  try {
    // Emit event to parent modal to refresh the franchise data
    eventBus.emit('franquias.refresh-single', props.franquiaId);
  } catch {
    // ignore
  }
};

const handleRemoveLocation = async (locationType: string, locationId: Id): Promise<void> => {
  try {
    // NOTE: contract `Localidades_ToggleRelacaoInput` declares lowercase keys
    // (type, localidade_id, franquia_id) but the legacy JS payload used
    // PascalCase. Preserving the original runtime payload to avoid behavior
    // change in this migration — flagged as latent bug for follow-up.
    const relacaoData = {
      Type: locationType,
      LocalidadeID: locationId,
      OwnerID: props.franquiaId,
      OwnerType: 'franquia',
    };

    await toggleRelacao(relacaoData as unknown as Parameters<typeof toggleRelacao>[0]);

    eventBus.emit('globalMessage.success', t('Área de atuação removida com sucesso'));

    // Refresh franchise data
    await refreshFranquia();

    // Emit event — cacheConfig invalidates permissions cache on 'location-removed'
    eventBus.emit('location-removed', { franquiaId: props.franquiaId });
  } catch {
    eventBus.emit('globalMessage.error', t('Erro ao remover área de atuação'));
  }
};

const handleLocationAdded = (): void => {
  void refreshFranquia();
  // Cache invalidation handled by cacheConfig listening to 'location-added'
};

// Listen for location events from centralized modals
onMounted(() => {
  eventBus.on('location-added', handleLocationAdded);
});

onBeforeUnmount(() => {
  eventBus.off('location-added', handleLocationAdded);
});
</script>

<style scoped>
/* PrimeVue Button handles styling */
</style>
